import React, { useEffect, useState } from 'react';
import { dbService, type AgentInfo } from '../lib/database';
import { Users, AlertTriangle } from 'lucide-react';

interface AgentsListProps {
  userEmail: string;
}

export function AgentsList({ userEmail }: AgentsListProps) {
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const profileData = await dbService.getProfileByEmail(userEmail);
        if (profileData?.agents_info) {
          setAgents(profileData.agents_info);
        }
      } catch (err) {
        setError('Failed to fetch agents');
        console.error('Error fetching agents:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, [userEmail]);

  if (loading) {
    return <div className="p-8 text-center text-zinc-400">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-500 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-emerald-400" />
        <h2 className="text-2xl font-bold text-white">My Agents</h2>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {agents.map((agent) => (
          <div
            key={agent.agent_id}
            className="bg-zinc-800/50 rounded-lg p-6 border border-zinc-700/50 hover:border-emerald-500/30 transition-all group"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                  {agent.display_name}
                </h3>
                <p className="text-sm text-zinc-400">Agent ID: {agent.agent_id}</p>
              </div>
            </div>
          </div>
        ))}

        {agents.length === 0 && (
          <div className="col-span-full text-center py-12 bg-zinc-800/30 rounded-lg border border-zinc-700/30">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No agents assigned yet</p>
          </div>
        )}
      </div>
    </div>
  );
}